import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { FileText, Trash2, Upload, ExternalLink } from 'lucide-react'

interface JobSheet {
  id: string
  title: string
  description?: string
  fileName: string
  fileSize: number
  positionId?: string
  positionName?: string
  createdAt: string
}

interface Position { id: string; name: string }

const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api'

export default function JobSheets() {
  const { current: establishment } = useEstablishment()
  const estId = establishment?.id

  const [sheets, setSheets] = useState<JobSheet[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState({ title: '', description: '', positionId: '' })
  const [selectedFile, setSelectedFile] = useState<{ name: string; data: string } | null>(null)

  const load = useCallback(async () => {
    if (!estId) return
    setLoading(true)
    try {
      const r = await api.get(`/job-sheets?establishmentId=${estId}`)
      setSheets(r.data || [])
    } finally { setLoading(false) }
  }, [estId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!estId) return
    api.get(`/positions?establishmentId=${estId}`).then(r => setPositions(r.data || [])).catch(() => {})
  }, [estId])

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') { alert('Seuls les fichiers PDF sont acceptés'); return }
    const reader = new FileReader()
    reader.onload = ev => {
      const dataUrl = ev.target?.result as string
      setSelectedFile({ name: file.name, data: dataUrl.split(',')[1] })
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const upload = async () => {
    if (!form.title || !selectedFile || !estId) return
    setUploading(true)
    try {
      const pos = positions.find(p => p.id === form.positionId)
      await api.post('/job-sheets', {
        establishmentId: estId,
        title: form.title,
        description: form.description || null,
        fileName: selectedFile.name,
        fileData: selectedFile.data,
        positionId: form.positionId || null,
        positionName: pos?.name || null,
      })
      setForm({ title: '', description: '', positionId: '' })
      setSelectedFile(null)
      load()
    } catch { alert('Erreur lors de l\'upload') }
    finally { setUploading(false) }
  }

  const deleteSheet = async (id: string) => {
    if (!confirm('Supprimer cette fiche de poste ?')) return
    await api.delete(`/job-sheets/${id}`)
    load()
  }

  const openPdf = (id: string) => {
    const token = localStorage.getItem('token')
    window.open(`${API_BASE}/job-sheets/${id}/pdf?token=${token}`, '_blank')
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Fiches de poste</h1>
        <p className="text-sm text-gray-500 mt-1">Procédures et fiches PDF accessibles par les employés</p>
      </div>

      {/* Formulaire d'upload */}
      <div className="bg-white rounded-xl border p-5 mb-6">
        <h3 className="font-semibold mb-4">Ajouter une fiche</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Titre *</label>
            <input placeholder="ex : Procédure ouverture caisse" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Poste concerné (optionnel)</label>
            <select value={form.positionId} onChange={e => setForm(f => ({ ...f, positionId: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">— Tous les employés —</option>
              {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-gray-500 mb-1 block">Description (optionnel)</label>
            <input placeholder="ex : À lire avant chaque ouverture" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        {/* Zone fichier PDF */}
        <div
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors mb-3 ${selectedFile ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
          <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFile} className="hidden" />
          {selectedFile ? (
            <div>
              <FileText size={24} className="mx-auto text-blue-500 mb-1" />
              <p className="text-sm font-semibold text-blue-700">{selectedFile.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">Cliquer pour changer</p>
            </div>
          ) : (
            <div>
              <Upload size={24} className="mx-auto text-gray-300 mb-1" />
              <p className="text-sm text-gray-500">Cliquer pour sélectionner un PDF</p>
            </div>
          )}
        </div>

        <button onClick={upload} disabled={!form.title || !selectedFile || uploading}
          className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {uploading ? 'Envoi en cours...' : 'Ajouter la fiche'}
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      ) : sheets.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <FileText size={40} className="mx-auto text-gray-200 mb-3" />
          <p>Aucune fiche de poste</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sheets.map(s => (
            <div key={s.id} className="bg-white rounded-xl border px-4 py-3.5 flex items-center gap-4">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{s.title}</p>
                {s.description && <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>}
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-gray-400">{s.fileName}</span>
                  <span className="text-xs text-gray-400">·</span>
                  <span className="text-xs text-gray-400">{s.fileSize} Ko</span>
                  {s.positionName && (
                    <>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{s.positionName}</span>
                    </>
                  )}
                  {!s.positionName && (
                    <>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Tous</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => openPdf(s.id)}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
                  <ExternalLink size={14} /> Voir
                </button>
                <button onClick={() => deleteSheet(s.id)}
                  className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
                  <Trash2 size={14} /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
